/**
 * Utility functions for formatting period strings and calculating date ranges
 */

/**
 * Calculate the date range for a given period string
 * Supports: 2026W06, 2025Q4, 2025M01, 2025Y
 */
export function getPeriodDateRange(period: string): { start: Date; end: Date } | null {
  // Daily format: 2026D139 (day-of-year)
  const dayMatch = period.match(/^(\d{4})D(\d{1,3})$/);
  if (dayMatch) {
    const year = parseInt(dayMatch[1], 10);
    const doy = parseInt(dayMatch[2], 10);
    // Jan 0 + doy gives the correct calendar date
    const date = new Date(year, 0, doy);
    return { start: date, end: date };
  }

  // Week format: 2026W06
  const weekMatch = period.match(/^(\d{4})W(\d{2})$/);
  if (weekMatch) {
    const year = parseInt(weekMatch[1], 10);
    const week = parseInt(weekMatch[2], 10);

    // ISO week date calculation
    const jan4 = new Date(year, 0, 4);
    const daysSinceMonday = (jan4.getDay() + 6) % 7;
    const firstMonday = new Date(year, 0, 4 - daysSinceMonday);

    const start = new Date(firstMonday);
    start.setDate(start.getDate() + (week - 1) * 7);

    const end = new Date(start);
    end.setDate(end.getDate() + 6);

    return { start, end };
  }

  // Quarter format: 2025Q4
  const quarterMatch = period.match(/^(\d{4})Q([1-4])$/);
  if (quarterMatch) {
    const year = parseInt(quarterMatch[1], 10);
    const quarter = parseInt(quarterMatch[2], 10);

    const start = new Date(year, (quarter - 1) * 3, 1);
    const end = new Date(year, quarter * 3, 0); // Last day of quarter

    return { start, end };
  }

  // Month format: 2025M01
  const monthMatch = period.match(/^(\d{4})M(\d{2})$/);
  if (monthMatch) {
    const year = parseInt(monthMatch[1], 10);
    const month = parseInt(monthMatch[2], 10);

    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0); // Last day of month

    return { start, end };
  }

  // Year format: 2025Y or 2025
  const yearMatch = period.match(/^(\d{4})Y?$/);
  if (yearMatch) {
    const year = parseInt(yearMatch[1], 10);

    const start = new Date(year, 0, 1);
    const end = new Date(year, 11, 31);

    return { start, end };
  }

  return null;
}

/**
 * Format a date in abbreviated format: "Feb 4, 2026"
 */
export function formatDateAbbreviated(date: Date): string {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

/**
 * Get formatted date range string for a period
 * Returns: "Feb 4, 2026 → Feb 9, 2026" or null if invalid period
 */
export function getFormattedPeriodDateRange(period: string): string | null {
  const range = getPeriodDateRange(period);
  if (!range) return null;

  if (range.start.getTime() === range.end.getTime()) {
    return formatDateAbbreviated(range.start);
  }
  return `${formatDateAbbreviated(range.start)} → ${formatDateAbbreviated(range.end)}`;
}

/**
 * Format a daily period (2026D139) as a compact badge label: "May 19"
 */
export function formatDailyPeriodLabel(period: string): string {
  const match = period.match(/^(\d{4})D(\d{1,3})$/);
  if (!match) return period;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const date = new Date(parseInt(match[1], 10), 0, parseInt(match[2], 10));
  return `${months[date.getMonth()]} ${date.getDate()}`;
}
